(() => {
  const ROOT_SELECTOR = '[data-pdp-color-linked-story]';
  const INITIALIZED_ATTR = 'data-pdp-color-linked-story-initialized';

  const normalize = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/grey/g, 'gray')
    .replace(/[^a-z0-9]+/g, '');

  const getProductRoot = (root) => {
    const productId = String(root.dataset.productId || '');
    const products = Array.from(document.querySelectorAll('xo-product'));

    return products.find((product) => String(product.getAttribute('xo-product-id') || '') === productId)
      || root.closest('.shopify-section')?.previousElementSibling?.querySelector?.('xo-product')
      || document.querySelector('xo-product');
  };

  const isColorField = (field, optionName) => {
    const requestedName = normalize(optionName || 'color');
    const name = normalize(field.getAttribute('name'));
    const ariaLabel = normalize(field.getAttribute('aria-label'));
    const xoLabel = normalize(field.getAttribute('xo-label'));

    if (requestedName && name.includes(requestedName)) return true;
    if (/colou?r/i.test(field.getAttribute('name') || '')) return true;

    const variantGroup = field.closest('.xo-product-variant');
    const groupName = variantGroup?.querySelector(
      '.xo-product-variant__inner-name > span:first-child, .xo-product-variant__top-name > span:first-child'
    )?.textContent;

    return normalize(groupName).includes(requestedName)
      || /colou?r/i.test(groupName || '')
      || Boolean(ariaLabel && xoLabel && ariaLabel === xoLabel && requestedName === 'color');
  };

  const getSelectedColor = (root) => {
    const productRoot = getProductRoot(root);
    if (!productRoot) return '';

    const optionName = root.dataset.colorOptionName || 'Color';
    const fields = Array.from(productRoot.querySelectorAll('xo-product-variants input, xo-product-variants select'));

    for (const field of fields) {
      if (!isColorField(field, optionName)) continue;

      if (field instanceof HTMLInputElement && field.checked) return field.value || field.getAttribute('xo-label') || '';
      if (field instanceof HTMLSelectElement) return field.value || '';
    }

    return '';
  };

  const stopVideo = (video) => {
    video.pause();
    video.removeAttribute('autoplay');
  };

  const playSelectedVideo = (root, media) => {
    const autoplay = root.dataset.autoplay === 'true';
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    media.querySelectorAll('video').forEach((video) => {
      if (!autoplay || reduceMotion || video.offsetParent === null) return;
      const playResult = video.play();
      if (playResult && typeof playResult.catch === 'function') playResult.catch(() => {});
    });
  };

  const loadSelectedImages = (media) => {
    media.querySelectorAll('img[data-color-story-lazy][data-color-story-src]').forEach((image) => {
      const source = image.dataset.colorStorySrc;
      if (!source) return;
      image.src = source;
      image.removeAttribute('data-color-story-src');
      image.removeAttribute('data-color-story-lazy');
    });
  };

  const mediaObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const root = entry.target;
          root.dataset.colorStoryInViewport = 'true';
          updateStory(root);
          observer.unobserve(root);
        });
      }, { rootMargin: '200px 0px' })
    : null;

  const updateStory = (root) => {
    const mediaItems = Array.from(root.querySelectorAll('[data-color-story-media]'));
    if (!mediaItems.length) return;

    const selectedColor = getSelectedColor(root);
    if (selectedColor) root.dataset.initialColor = selectedColor;
    const color = normalize(selectedColor || root.dataset.initialColor);
    const defaultMedia = root.querySelector('[data-color-story-default]') || mediaItems[0];
    const selectedMedia = mediaItems.find((media) => normalize(media.dataset.colorStoryColor) === color) || defaultMedia;

    if (root.dataset.colorStoryInViewport === 'true') {
      loadSelectedImages(selectedMedia);
    }

    mediaItems.forEach((media) => {
      const isSelected = media === selectedMedia;
      media.hidden = !isSelected;
      media.setAttribute('aria-hidden', String(!isSelected));
      if (!isSelected) media.querySelectorAll('video').forEach(stopVideo);
    });

    selectedMedia.dataset.colorStoryActive = color || 'default';
    if (root.dataset.colorStoryInViewport === 'true') {
      playSelectedVideo(root, selectedMedia);
    }
  };

  const scheduleUpdate = (root) => {
    window.requestAnimationFrame(() => updateStory(root));
  };

  const initRoot = (root) => {
    if (root.hasAttribute(INITIALIZED_ATTR)) return;

    const productRoot = getProductRoot(root);
    const observer = productRoot
      ? new MutationObserver(() => scheduleUpdate(root))
      : null;

    observer?.observe(productRoot, { childList: true, subtree: true });
    root.setAttribute(INITIALIZED_ATTR, 'true');

    if (mediaObserver) {
      mediaObserver.observe(root);
    } else {
      root.dataset.colorStoryInViewport = 'true';
    }

    updateStory(root);
  };

  const initAll = () => document.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  const refreshAll = () => {
    initAll();
    document.querySelectorAll(ROOT_SELECTOR).forEach(updateStory);
  };

  if (!window.CortailorColorLinkedStory) {
    window.CortailorColorLinkedStory = { initAll, refreshAll };

    document.addEventListener('input', () => window.setTimeout(window.CortailorColorLinkedStory.refreshAll, 0), true);
    document.addEventListener('change', () => window.setTimeout(window.CortailorColorLinkedStory.refreshAll, 0), true);
    document.addEventListener('shopify:section:load', initAll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => window.CortailorColorLinkedStory.initAll(), { once: true });
  } else {
    window.CortailorColorLinkedStory.initAll();
  }
})();
